import { doc, getFirestore, runTransaction, setDoc, getDoc, getDocs, collection, query, where, addDoc, deleteDoc, serverTimestamp, updateDoc, Timestamp } from 'firebase/firestore';

// Detect a JSON-deserialized Firestore Timestamp — `{_seconds: N}` or
// `{_seconds, _nanoseconds}` — that arrived via an HTTP round-trip and
// would silently corrupt the field if written back as-is. Used by the
// flatten guard in updateBuyer (and is a generic helper any other
// dotted-path writer can adopt). Object shape is strict: only the two
// known keys, both numbers.
const isSerializedTimestamp = (v) => {
	if (v == null || typeof v !== 'object' || Array.isArray(v) || v instanceof Date) return false;
	if (typeof v._seconds !== 'number') return false;
	const keys = Object.keys(v);
	return keys.every(k => k === '_seconds' || k === '_nanoseconds');
};
const rehydrateTimestamp = (v) => Timestamp.fromMillis(v._seconds * 1000 + Math.round((v._nanoseconds || 0) / 1e6));

// Cache for getDocument - stores document data with timestamp
const documentCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

const getCacheKey = (colRef, docRef) => `${colRef}/${docRef}`;

const getCachedDocument = (colRef, docRef) => {
	const cacheKey = getCacheKey(colRef, docRef);
	const cached = documentCache.get(cacheKey);
	if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
		return cached.data;
	}
	return null;
};

const setCachedDocument = (colRef, docRef, data) => {
	const cacheKey = getCacheKey(colRef, docRef);
	documentCache.set(cacheKey, {
		data,
		timestamp: Date.now()
	});
};

export const clearDocumentCache = () => {
	documentCache.clear();
};

export const setData = (colRef, docRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		setDoc(doc(db, colRef, docRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			if (process.env.NODE_ENV === 'development') {
				console.log("Set data error = ", e);
				console.log("Set data error data = ", data);
			}
			resolve(res);
		});
	});
}

export const setSubData = (colRef, docRef, subColRef, subDocRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		setDoc(doc(db, colRef, docRef, subColRef, subDocRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			if (process.env.NODE_ENV === 'development') {
				console.log("Error = ", e);
			}
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			resolve(res);
		});
	});
};

export const mergeSubData = (colRef, docRef, subColRef, subDocRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		setDoc(doc(db, colRef, docRef, subColRef, subDocRef), {
			data
		}, { merge: true })
		.then(() => {
			const res = {
				status: 200,
				message: "Document merged successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			if (process.env.NODE_ENV === 'development') {
				console.log("Error = ", e);
			}
			const res = {
				status: 400,
				message: `Error in merging document ${e}`
			};
			resolve(res);
		});
	});
};

export const setDataRandomId = (colRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		addDoc(collection(db, colRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			resolve(res);
		});
	});
}

export const getDocument = async (colRef, docRef, forceRefresh = false) => {
	// Check cache first (unless force refresh)
	if (!forceRefresh) {
		const cached = getCachedDocument(colRef, docRef);
		if (cached) {
			return cached;
		}
	}

	const db = getFirestore();
	const query = doc(db, colRef, docRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				// Cache successful responses
				setCachedDocument(colRef, docRef, res);
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
};

export const getSubDocument = (colRef, docRef, subColRef, subDocRef) => {
	const db = getFirestore();
	const query = doc(db, colRef, docRef, subColRef, subDocRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
}

export const getSubSubDocument = (colRef, docRef, subColRef, subDocRef, subSubColRef, subSubDocRef) => {
	const db = getFirestore();
	const query = doc(db, colRef, docRef, subColRef, subDocRef, subSubColRef, subSubDocRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
}

export const getAllDocuments = async(colRef) => {
	const db = getFirestore();
	const array = [];
	const ids = [];

	return new Promise(async resolve => {
		await getDocs(collection(db, colRef))
		.then((querySnapshot) => {
			querySnapshot.forEach((doc) => {
				const data = doc.data();
				array.push(data.data);
				ids.push(doc.id);
			});
			const res = {
				status: 200,
				data: array,
				ids: ids,
				message: "Document found!"
			}
			resolve(res);
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				ids: [],
				message: error,
				code: error.code
			}
			resolve(res);
		});
	});
}

export const getAllSubDocuments = async(colRef, docRef, subColRef) => {
	const db = getFirestore();
	const array = [];
	const ids = [];

	return new Promise(async resolve => {
		await getDocs(collection(db, colRef, docRef, subColRef))
		.then((querySnapshot) => {
			querySnapshot.forEach((doc) => {
				const data = doc.data();
				array.push(data.data);
				ids.push(doc.id);
			});
			const res = {
				status: 200,
				data: array,
				ids: ids,
				message: "Document found!"
			}
			resolve(res);
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				ids: [],
				message: error,
				code: error.code
			}
			resolve(res);
		});
	});
}

export const getWhereDoc = async(col, field, operator, val) => {
	const db = getFirestore();
	const userRef = collection(db, col);
	const whereQuery = await query(userRef, where(field, operator, val));
	const queryDoc = await getDocs(whereQuery);
	
	const response = [];
	queryDoc.forEach((doc) => {
		const data = doc.data();
		response.push(data.data);
	});

	const res = {
		status: response.length === 0 ? 400 : 200,
		data: response
	};
	
	return res;
};

export const setTransaction = async (colRef, docRef, root, val, subval) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				root === null ?
					data = val
				:
				subval === null ?
					data[root] = val
				:
					data[root][subval] = val
				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
}

/**
 * Server-side "now" sentinel for a Firestore write — use inside the value passed
 * to setCompanyFeature when a timestamp must come from Firestore's clock, not the
 * browser's (e.g. autoSendToCrm.enabledAt, which the backend compares against
 * lead createdAt — both server clocks, so no skew).
 */
export const serverNow = () => serverTimestamp();

/**
 * Write ONE company feature flag/knob: companies/{companyId}.data.features.{key}.
 * The value replaces that key wholesale (pass the complete object for map-valued
 * features). Dotted-path update, so a company doc with no `features` map yet gets
 * one created; sibling features are untouched. Backend readers:
 * functions/utils/companyFeatures.js + functions/autoSend/autoSendPolicy.js.
 */
export const setCompanyFeature = async (companyId, key, value) => {
	if (!companyId || !key) return { status: 400, message: 'companyId and key are required' };
	const db = getFirestore();
	try {
		await updateDoc(doc(db, 'companies', companyId), { [`data.features.${key}`]: value });
		return { status: 200, message: 'Feature saved' };
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			console.error('setCompanyFeature error =', e);
		}
		return { status: 400, message: `Error saving feature: ${e}` };
	}
};

/**
 * Custom transaction function specifically for updating ghl_triggers
 * Reads the existing triggers array and replaces it with the new array atomically
 * @param {string} companyId - The Clozers companyId (document ID)
 * @param {Array} newTriggersArray - The new array of trigger objects to save
 * @returns {Promise<Object>} - Result object with status and message
 */
export const updateGHLTriggers = async (companyId, newTriggersArray) => {
	const db = getFirestore();
	const colRef = "ghl_triggers";
	const queryParams = doc(db, colRef, companyId);
	
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			
			if (!query.exists()) {
				// Document doesn't exist - create it
				transaction.set(queryParams, {
					data: Array.isArray(newTriggersArray) ? newTriggersArray : []
				});
			} else {
				// Document exists - update the data field with new triggers array
				transaction.update(queryParams, {
					data: Array.isArray(newTriggersArray) ? newTriggersArray : []
				});
			}
		});

		return {
			status: 200,
			message: "GHL triggers updated successfully"
		};
	} 
	catch (e) {
		return {
			status: 400,
			message: `Error updating GHL triggers: ${e}`
		};
	}
}

/**
 * Track a recently viewed report
 * Stores the report in recently_viewed/{companyId}/users/{userId}
 * @param {string} companyId - The company ID
 * @param {string} userId - The user ID
 * @param {Object} propertyData - Property data containing propertyId, countyId, and address
 * @param {number} maxItems - Maximum number of items to keep (default: 10)
 * @returns {Promise<Object>} - Result object with status and message
 */
export const trackRecentlyViewed = async (companyId, userId, propertyData, maxItems = 10) => {
	if (!companyId || !userId || !propertyData) {
		return {
			status: 400,
			message: "Missing required parameters"
		};
	}

	const db = getFirestore();
	const colRef = "recently_viewed";
	const docRef = companyId;
	const subColRef = "users";
	const subDocRef = userId;
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);

	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			
			const newItem = {
				propertyId: propertyData.propertyId,
				countyId: propertyData.countyId,
				address: propertyData.address || {
					label: propertyData.label || `${propertyData.streetAddress || ''}, ${propertyData.city || ''}, ${propertyData.state || ''} ${propertyData.postalCode || ''}`.trim(),
					streetAddress: propertyData.streetAddress,
					city: propertyData.city,
					state: propertyData.state,
					postalCode: propertyData.postalCode
				},
				bedrooms: propertyData.bedrooms || 0,
				bathrooms: propertyData.bathrooms || 0,
				livingSquareFootage: propertyData.livingSquareFootage || 0,
				propertyType: propertyData.propertyType || null,
				images: propertyData.images || null,
				timestamp: new Date(),
				companyId: companyId
			};

			if (!query.exists()) {
				// Document doesn't exist - create it with the new item
				transaction.set(queryParams, {
					data: {
						items: [newItem]
					}
				});
			} else {
				// Document exists - update the items array
				const data = query.data().data;
				let items = data.items || [];

				// Remove existing entry with same propertyId if it exists
				items = items.filter(item => item.propertyId !== propertyData.propertyId);

				// Add new item at the beginning
				items.unshift(newItem);

				// Limit to maxItems
				if (items.length > maxItems) {
					items = items.slice(0, maxItems);
				}

				transaction.update(queryParams, {
					data: {
						items: items
					}
				});
			}
		});

		return {
			status: 200,
			message: "Recently viewed tracked successfully"
		};
	} 
	catch (e) {
		return {
			status: 400,
			message: `Error tracking recently viewed: ${e}`
		};
	}
}

/**
 * Get recently viewed reports for a user
 * @param {string} companyId - The company ID
 * @param {string} userId - The user ID
 * @param {number} limit - Maximum number of items to return (default: 10)
 * @returns {Promise<Object>} - Result object with status and data array
 */
export const getRecentlyViewed = async (companyId, userId, limit = 10) => {
	if (!companyId || !userId) {
		return {
			status: 400,
			data: [],
			message: "Missing required parameters"
		};
	}

	const db = getFirestore();
	const colRef = "recently_viewed";
	const docRef = companyId;
	const subColRef = "users";
	const subDocRef = userId;
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);

	return new Promise(async resolve => {
		await getDoc(queryParams)
			.then((docSnapshot) => {
				if (docSnapshot.exists()) {
					const data = docSnapshot.data().data;
					const items = data.items || [];
					// Return limited items, sorted by timestamp (most recent first)
					const sortedItems = items
						.sort((a, b) => {
							const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
							const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
							return timeB - timeA;
						})
						.slice(0, limit);
					
					const res = {
						status: 200,
						data: sortedItems,
						message: "Recently viewed found!"
					};
					resolve(res);
				} else {
					const res = {
						status: 200,
						data: [],
						message: "No recently viewed items"
					};
					resolve(res);
				}
			})
			.catch((error) => {
				const res = {
					status: 400,
					data: [],
					message: `Error getting recently viewed: ${error}`,
					code: error.code
				};
				resolve(res);
			});
	});
}

export const deleteDocument = async (colRef, docRef) => {
	const db = getFirestore();
	const docRefToDelete = doc(db, colRef, docRef);

	return new Promise(async resolve => {
		await deleteDoc(docRefToDelete)
		.then(() => {
			const res = {
				status: 200,
				message: "Document deleted successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			const res = {
				status: 400,
				message: `Error deleting document: ${e}`
			};
			if (process.env.NODE_ENV === 'development') {
				console.log("Delete document error = ", e);
			}
			resolve(res);
		});
	});
}

/**
 * Delete a loading document for a property
 * Deletes the document at companies/{companyId}/loading/{propertyId}
 * @param {string} companyId - The company ID
 * @param {string} propertyId - The property ID
 * @returns {Promise<Object>} - Result object with status and message
 */
export const deleteLoadingDocument = async (companyId, propertyId) => {
	if (!companyId || !propertyId) {
		return {
			status: 400,
			message: "Missing required parameters: companyId and propertyId"
		};
	}

	const db = getFirestore();
	const loadingDocRef = doc(db, "companies", companyId, "loading", propertyId);

	return new Promise(async resolve => {
		await deleteDoc(loadingDocRef)
			.then(() => {
				const res = {
					status: 200,
					message: "Loading document deleted successfully"
				};
				resolve(res);
			})
			.catch((e) => {
				// If document doesn't exist, that's fine - it means there's no lock
				// Only log actual errors, not "not found" errors
				if (e.code !== 'not-found' && process.env.NODE_ENV === 'development') {
					console.log("Delete loading document error = ", e);
				}
				const res = {
					status: 200, // Return success even if doc doesn't exist
					message: "Loading document deleted or did not exist"
				};
				resolve(res);
			});
	});
}


// ============================================================================
// AUTOMATIONS SUBCOLLECTION FUNCTIONS
// New unified structure: automations/{companyId}/automations/{automationId}
// ============================================================================

/**
 * Get all automations for a company
 * Reads from automations/{companyId}/automations
 * @param {string} companyId - The company ID
 * @param {string} [type] - Optional type filter ('api', 'mls_buy_box', 'x_post', etc.)
 * @returns {Promise<Object>} - Result object with status, data (array), and message
 */
export const getAutomations = async (companyId, type = null) => {
	if (!companyId) {
		console.error('getAutomations: Missing companyId');
		return {
			status: 400,
			data: [],
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const automationsRef = collection(db, "automations", companyId, "automations");

	try {
		const snapshot = await getDocs(automationsRef);
		
		const automations = [];
		
		snapshot.forEach((docSnap) => {
			const data = docSnap.data();
			automations.push({
				id: docSnap.id,
				...data
			});
		});

		const filteredAutomations = type
			? automations.filter(automation => (
				Array.isArray(automation.ingestion)
				&& automation.ingestion.some(item => item && item.type === type)
			))
			: automations;

		if (process.env.NODE_ENV === 'development') {
			console.log(`getAutomations: Found ${filteredAutomations.length} automation(s) for company ${companyId}${type ? ` (type: ${type})` : ''}`);
		}
		return {
			status: 200,
			data: filteredAutomations,
			message: `Found ${filteredAutomations.length} automation(s)`
		};
	} catch (error) {
		// Always log errors, even in production
		console.error(`getAutomations: Error for company ${companyId}:`, error);
		return {
			status: 400,
			data: [],
			message: `Error getting automations: ${error.message || error}`,
			code: error.code
		};
	}
};

/**
 * Get a single automation by ID
 * Reads from automations/{companyId}/automations/{automationId}
 * @param {string} companyId - The company ID
 * @param {string} automationId - The automation ID
 * @returns {Promise<Object>} - Result object with status, data, and message
 */
export const getAutomation = async (companyId, automationId) => {
	if (!companyId || !automationId) {
		return {
			status: 400,
			data: null,
			message: "Missing required parameter: companyId or automationId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "automations", companyId, "automations", automationId);

	try {
		const docSnap = await getDoc(docRef);
		if (docSnap.exists()) {
			return {
				status: 200,
				data: {
					id: docSnap.id,
					...docSnap.data()
				},
				message: "Automation found"
			};
		} else {
			return {
				status: 404,
				data: null,
				message: "Automation not found"
			};
		}
	} catch (error) {
		return {
			status: 400,
			data: null,
			message: `Error getting automation: ${error}`,
			code: error.code
		};
	}
};

/**
 * Create a new automation for a company
 * Writes to automations/{companyId}/automations/{auto_generated_id}
 * @param {string} companyId - The company ID
 * @param {Object} data - The automation configuration data
 * @returns {Promise<Object>} - Result object with status, automationId, and message
 */
export const createAutomation = async (companyId, data) => {
	if (!companyId) {
		return {
			status: 400,
			automationId: null,
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const automationsRef = collection(db, "automations", companyId, "automations");

	try {
		const dataWithTimestamps = {
			...data,
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp()
		};

		const docRef = await addDoc(automationsRef, dataWithTimestamps);

		return {
			status: 201,
			automationId: docRef.id,
			message: "Automation created successfully"
		};
	} catch (error) {
		return {
			status: 400,
			automationId: null,
			message: `Error creating automation: ${error}`
		};
	}
};

/**
 * Update an existing automation
 * Writes to automations/{companyId}/automations/{automationId}
 * Uses merge to allow partial updates
 * @param {string} companyId - The company ID
 * @param {string} automationId - The automation ID
 * @param {Object} data - The automation configuration data
 * @returns {Promise<Object>} - Result object with status and message
 */
export const setAutomation = async (companyId, automationId, data) => {
	if (!companyId || !automationId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId or automationId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "automations", companyId, "automations", automationId);

	try {
		const dataWithTimestamps = {
			...data,
			updatedAt: serverTimestamp()
		};

		await setDoc(docRef, dataWithTimestamps, { merge: true });

		return {
			status: 200,
			message: "Automation updated successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error updating automation: ${error}`
		};
	}
};

/**
 * Delete an automation
 * Deletes from automations/{companyId}/automations/{automationId}
 * @param {string} companyId - The company ID
 * @param {string} automationId - The automation ID
 * @returns {Promise<Object>} - Result object with status and message
 */
export const deleteAutomation = async (companyId, automationId) => {
	if (!companyId || !automationId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId or automationId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "automations", companyId, "automations", automationId);

	try {
		await deleteDoc(docRef);

		return {
			status: 200,
			message: "Automation deleted successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error deleting automation: ${error}`
		};
	}
};

/**
 * Update automation using transaction (for atomic field updates)
 * @param {string} companyId - The company ID
 * @param {string} automationId - The automation ID
 * @param {string} field - Top-level field to update (e.g., 'active', 'name')
 * @param {any} value - New value for the field
 * @returns {Promise<Object>} - Result object with status and message
 */
export const setAutomationTransaction = async (companyId, automationId, field, value) => {
	if (!companyId || !automationId || !field) {
		return {
			status: 400,
			message: "Missing required parameters"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "automations", companyId, "automations", automationId);

	try {
		await runTransaction(db, async (transaction) => {
			const docSnap = await transaction.get(docRef);
			if (!docSnap.exists()) {
				throw new Error("Automation does not exist");
			}

			const updateData = {
				[field]: value,
				updatedAt: serverTimestamp()
			};

			transaction.update(docRef, updateData);
		});

		return {
			status: 200,
			message: "Automation updated successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error updating automation: ${error.message || error}`
		};
	}
};

// ============================================================================
// SETTINGS / UNDERWRITING FUNCTIONS
// ============================================================================

/**
 * Get underwriting settings for a company
 * Reads from settings/{companyId}
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} - Result object with status, data, and message
 */
export const getUnderwritingSettings = async (companyId) => {
	if (!companyId) {
		return {
			status: 400,
			data: null,
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "settings", companyId);

	// Default underwriting values (matches backend defaults)
	const defaultUnderwriting = {
		annualInterestRate: 0.12,  // 12%
		holdMonths: 4,
		titleRate: 0.015,          // 1.5%
		commissionRate: 0.04,      // 4%
		pointsRate: 0.02,          // 2%
		downPayment: 0,            // 0%
		targetProfitPercentage: 0.10 // 10%
	};

	try {
		const docSnap = await getDoc(docRef);
		if (docSnap.exists()) {
			const data = docSnap.data().data || {};
			const underwriting = data.underwriting || {};
			
			// Merge with defaults to ensure all fields exist
			return {
				status: 200,
				data: {
					...defaultUnderwriting,
					...underwriting
				},
				message: "Underwriting settings found"
			};
		} else {
			// Return defaults if no settings document exists
			return {
				status: 200,
				data: defaultUnderwriting,
				message: "Using default underwriting settings"
			};
		}
	} catch (error) {
		console.error(`getUnderwritingSettings: Error for company ${companyId}:`, error);
		return {
			status: 400,
			data: defaultUnderwriting,
			message: `Error getting underwriting settings: ${error}`,
			code: error.code
		};
	}
};

/**
 * Update underwriting settings for a company
 * Writes to settings/{companyId}/data/underwriting
 * @param {string} companyId - The company ID
 * @param {Object} underwritingData - The underwriting settings to save
 * @returns {Promise<Object>} - Result object with status and message
 */
export const setUnderwritingSettings = async (companyId, underwritingData) => {
	if (!companyId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "settings", companyId);

	try {
		// Get existing settings to merge with
		const docSnap = await getDoc(docRef);
		let existingData = {};
		
		if (docSnap.exists()) {
			existingData = docSnap.data().data || {};
		}

		// Update underwriting while preserving other settings
		await setDoc(docRef, {
			data: {
				...existingData,
				underwriting: underwritingData
			}
		}, { merge: true });

		return {
			status: 200,
			message: "Underwriting settings updated successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error updating underwriting settings: ${error}`
		};
	}
};

// ============================================================================
// BUYERS SUBCOLLECTION FUNCTIONS
// Structure: companies/{companyId}/buyers/{buyerId}
// ============================================================================

/**
 * Get all buyers for a company
 * @param {string} companyId - The company ID
 * @param {boolean} activeOnly - If true, only return active buyers (default: false)
 * @returns {Promise<Object>} - Result object with status, data (array), and message
 */
export const getBuyers = async (companyId, activeOnly = false) => {
	if (!companyId) {
		return {
			status: 400,
			data: [],
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const buyersRef = collection(db, "companies", companyId, "buyers");

	try {
		const snapshot = await getDocs(buyersRef);
		const buyers = [];

		snapshot.forEach((docSnap) => {
			const data = docSnap.data().data;
			// Apply active filter if requested
			if (!activeOnly || data.active !== false) {
				buyers.push({
					id: docSnap.id,
					...data
				});
			}
		});

		return {
			status: 200,
			data: buyers,
			message: `Found ${buyers.length} buyer(s)`
		};
	} catch (error) {
		console.error(`getBuyers: Error for company ${companyId}:`, error);
		return {
			status: 400,
			data: [],
			message: `Error getting buyers: ${error.message || error}`,
			code: error.code
		};
	}
};

/**
 * Get a single buyer by ID
 * @param {string} companyId - The company ID
 * @param {string} buyerId - The buyer ID
 * @returns {Promise<Object>} - Result object with status, data, and message
 */
export const getBuyer = async (companyId, buyerId) => {
	if (!companyId || !buyerId) {
		return {
			status: 400,
			data: null,
			message: "Missing required parameter: companyId or buyerId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "companies", companyId, "buyers", buyerId);

	try {
		const docSnap = await getDoc(docRef);
		if (docSnap.exists()) {
			return {
				status: 200,
				data: {
					id: docSnap.id,
					...docSnap.data().data
				},
				message: "Buyer found"
			};
		} else {
			return {
				status: 404,
				data: null,
				message: "Buyer not found"
			};
		}
	} catch (error) {
		return {
			status: 400,
			data: null,
			message: `Error getting buyer: ${error}`,
			code: error.code
		};
	}
};

// Mirrors functions/utils/buyerValidation.js's normalizeEmail/normalizePhone —
// duplicated rather than imported because that module lives outside src/,
// which CRA's build refuses to import from. findDuplicateBuyerIndexed
// (the Cloud Function side) queries data.contact.normalizedEmail/
// normalizedPhone directly, so every path that writes contact.email/phone
// from the app — createBuyer, updateBuyer, and this file's own
// findDuplicateBuyer below — needs to compute these the same way, or a
// buyer added/edited here silently falls out of that index.
const normalizeEmailForLookup = (email) => {
	if (!email || typeof email !== "string") return null;
	const trimmed = email.trim().toLowerCase();
	return trimmed.length > 0 ? trimmed : null;
};

const normalizePhoneForLookup = (phone) => {
	if (!phone) return null;
	const digits = ("" + phone).replace(/\D/g, "");
	return digits.length >= 10 ? digits : null;
};

/**
 * Create a new buyer for a company
 * @param {string} companyId - The company ID
 * @param {Object} buyerData - The buyer data
 * @returns {Promise<Object>} - Result object with status, buyerId, and message
 */
export const createBuyer = async (companyId, buyerData) => {
	if (!companyId) {
		return {
			status: 400,
			buyerId: null,
			message: "Missing required parameter: companyId"
		};
	}

	const db = getFirestore();
	const buyersRef = collection(db, "companies", companyId, "buyers");

	try {
		const contact = buyerData.contact ? {
			...buyerData.contact,
			normalizedEmail: normalizeEmailForLookup(buyerData.contact.email),
			normalizedPhone: normalizePhoneForLookup(buyerData.contact.phone),
		} : buyerData.contact;

		const dataWithTimestamps = {
			...buyerData,
			...(contact ? { contact } : {}),
			active: buyerData.active !== false, // Default to true
			wantsDeals: buyerData.wantsDeals !== false, // Default to true
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp()
		};

		const docRef = await addDoc(buyersRef, { data: dataWithTimestamps });

		// Update the document to include buyerId
		await setDoc(docRef, { data: { ...dataWithTimestamps, buyerId: docRef.id } });

		return {
			status: 201,
			buyerId: docRef.id,
			message: "Buyer created successfully"
		};
	} catch (error) {
		return {
			status: 400,
			buyerId: null,
			message: `Error creating buyer: ${error}`
		};
	}
};

/**
 * Check for duplicate buyer in an already-loaded buyers array by email or phone.
 * @param {Array} buyers - Array of buyer objects (with contact.email, contact.phone)
 * @param {{ email?: string, phone?: string }} contact - The new buyer's contact info
 * @returns {{ isDuplicate: boolean, matchedField: string|null, matchedBuyerName: string|null }}
 */
export const findDuplicateBuyer = (buyers, contact) => {
	const normEmail = contact?.email?.trim().toLowerCase() || null;
	const rawPhone = contact?.phone ? ('' + contact.phone).replace(/\D/g, '') : null;
	const normPhone = rawPhone && rawPhone.length >= 10 ? rawPhone : null;

	if (!normEmail && !normPhone) {
		return { isDuplicate: false, matchedField: null, matchedBuyerName: null };
	}

	for (const buyer of buyers) {
		const existing = buyer.contact || {};
		if (normEmail) {
			const existingEmail = existing.email?.trim().toLowerCase() || null;
			if (existingEmail === normEmail) {
				return { isDuplicate: true, matchedField: 'email', matchedBuyerName: existing.name || 'Unknown' };
			}
		}
		if (normPhone) {
			const existingPhone = existing.phone ? ('' + existing.phone).replace(/\D/g, '') : null;
			if (existingPhone && existingPhone.length >= 10 && existingPhone === normPhone) {
				return { isDuplicate: true, matchedField: 'phone', matchedBuyerName: existing.name || 'Unknown' };
			}
		}
	}

	return { isDuplicate: false, matchedField: null, matchedBuyerName: null };
};

/**
 * Update an existing buyer
 * @param {string} companyId - The company ID
 * @param {string} buyerId - The buyer ID
 * @param {Object} buyerData - The buyer data to update
 * @returns {Promise<Object>} - Result object with status and message
 */
export const updateBuyer = async (companyId, buyerId, buyerData) => {
	if (!companyId || !buyerId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId or buyerId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "companies", companyId, "buyers", buyerId);

	try {
		const flattenToDotPaths = (obj, prefix = "data") => {
			const result = {};
			Object.entries(obj || {}).forEach(([key, value]) => {
				if (value === undefined) return;
				const path = `${prefix}.${key}`;
				// Critical: detect JSON-deserialized Timestamps BEFORE the
				// recursive isPlainObject branch. Without this guard, an
				// `{_seconds, _nanoseconds}` payload would recurse and write
				// `data.X._seconds` / `data.X._nanoseconds` as separate
				// fields — turning the field into a plain map and breaking
				// native Firestore orderBy. Convert to a real Timestamp
				// instead so writes round-trip correctly.
				if (isSerializedTimestamp(value)) {
					result[path] = rehydrateTimestamp(value);
					return;
				}
				const isPlainObject = value !== null &&
					typeof value === "object" &&
					!Array.isArray(value) &&
					!(value instanceof Date);
				if (isPlainObject) {
					Object.assign(result, flattenToDotPaths(value, path));
				} else {
					result[path] = value;
				}
			});
			return result;
		};

		// Keep the denormalized lookup fields in sync whenever this update
		// touches contact.email/phone — buyerData.contact here is often a
		// PARTIAL patch (e.g. BuyersTable's inline-edit sends just
		// {contact: {email: newValue}}), so only stamp the field whose key is
		// actually present; guessing at the other would write a normalized
		// value computed from `undefined` over a perfectly fine existing one.
		let patchedBuyerData = buyerData;
		if (buyerData?.contact && typeof buyerData.contact === "object") {
			const contactPatch = { ...buyerData.contact };
			if ("email" in contactPatch) contactPatch.normalizedEmail = normalizeEmailForLookup(contactPatch.email);
			if ("phone" in contactPatch) contactPatch.normalizedPhone = normalizePhoneForLookup(contactPatch.phone);
			patchedBuyerData = { ...buyerData, contact: contactPatch };
		}

		const updatePayload = {
			...flattenToDotPaths(patchedBuyerData),
			"data.buyerId": buyerId,
			"data.updatedAt": serverTimestamp()
		};

		await updateDoc(docRef, updatePayload);

		return {
			status: 200,
			message: "Buyer updated successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error updating buyer: ${error}`
		};
	}
};

/**
 * Delete a buyer
 * @param {string} companyId - The company ID
 * @param {string} buyerId - The buyer ID
 * @returns {Promise<Object>} - Result object with status and message
 */
export const deleteBuyer = async (companyId, buyerId) => {
	if (!companyId || !buyerId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId or buyerId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "companies", companyId, "buyers", buyerId);

	try {
		await deleteDoc(docRef);

		return {
			status: 200,
			message: "Buyer deleted successfully"
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error deleting buyer: ${error}`
		};
	}
};

/**
 * Toggle buyer active status
 * @param {string} companyId - The company ID
 * @param {string} buyerId - The buyer ID
 * @param {boolean} active - New active status
 * @returns {Promise<Object>} - Result object with status and message
 */

export const setBuyerActive = async (companyId, buyerId, active) => {
	if (!companyId || !buyerId) {
		return {
			status: 400,
			message: "Missing required parameter: companyId or buyerId"
		};
	}

	const db = getFirestore();
	const docRef = doc(db, "companies", companyId, "buyers", buyerId);

	try {
		await updateDoc(docRef, {
			"data.active": active,
			"data.updatedAt": serverTimestamp()
		});

		return {
			status: 200,
			message: `Buyer ${active ? 'activated' : 'deactivated'} successfully`
		};
	} catch (error) {
		return {
			status: 400,
			message: `Error updating buyer status: ${error}`
		};
	}
};

